import { Menu } from "lucide-react";
import { Link, NavLink } from "react-router";

import { useFeatureFlag } from "@punto/frontend-shared/hooks/use-feature-flag";
import { useTranslation } from "@punto/frontend-shared/i18n/i18next";
import { Badge } from "@punto/ui/components/badge";
import { Button } from "@punto/ui/components/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@punto/ui/components/dropdown-menu";
import { Sheet, SheetContent, SheetTrigger } from "@punto/ui/components/sheet";

import { EXTENSION_STORE_URL } from "@/lib/extension/extension-constants";
import { Can } from "@/modules/abilities";
import { OrganizationSwitcher } from "@/modules/auth/components/organization-switcher";
import { useUser } from "@/modules/auth/hooks/use-user";
import { UserAvatar } from "@/modules/users/components/user-avatar";
import { routePaths } from "@/router/route-paths";

import { NewProcedureButton } from "./new-procedure-button";
import { type NavItem, useNav } from "./use-nav";

// An optional counter next to a nav label — today only the inbox has one.
// Zero never reaches here: `useNav` leaves `badge` undefined instead, so an
// empty inbox shows no dot at all.
//
// A `div` and not a `span`: `Badge` renders a `div`, which is flow content and
// so cannot live inside a phrasing-only element. The bare number also says
// nothing on its own, hence the sr-only word that gives it meaning.
const NavItemLabel = ({ item }: { item: NavItem }) => {
  const { t } = useTranslation();

  return (
    <div className="flex items-center gap-2">
      {item.label}
      {item.badge === undefined ? null : (
        <Badge variant="secondary">
          {item.badge}
          <span className="sr-only">{t("Header.menu_badge_pending")}</span>
        </Badge>
      )}
    </div>
  );
};

const DesktopNav = () => {
  const { menuItems, homeLink } = useNav();

  return (
    <nav className="hidden flex-col gap-6 text-lg font-medium md:flex md:flex-row md:items-center md:gap-5 md:text-sm lg:gap-6">
      <Link
        to={homeLink.href}
        className="flex flex-nowrap items-center gap-2 text-lg font-semibold md:text-base"
      >
        <span className="whitespace-nowrap">{homeLink.label}</span>
      </Link>
      {menuItems.map((item) => (
        <NavLink
          key={item.href}
          to={item.href}
          className="text-muted-foreground transition-colors hover:text-foreground [&.active]:text-foreground"
        >
          <NavItemLabel item={item} />
        </NavLink>
      ))}
    </nav>
  );
};

const MobileNav = () => {
  const { menuItems, homeLink } = useNav();

  return (
    <nav className="grid gap-6 text-lg font-medium">
      <Link
        to={homeLink.href}
        className="flex items-center gap-2 text-lg font-semibold"
      >
        <span>{homeLink.label}</span>
      </Link>
      {menuItems.map((item) => (
        <NavLink
          key={item.href}
          to={item.href}
          className="text-muted-foreground transition-colors hover:text-foreground [&.active]:text-foreground"
        >
          <NavItemLabel item={item} />
        </NavLink>
      ))}
    </nav>
  );
};

const Header = () => {
  const { t } = useTranslation();
  const { user } = useUser();
  const agencyExportEnabled = useFeatureFlag("agency-export");

  return (
    <header className="sticky top-0 z-10 flex h-16 items-center gap-4 border-b bg-background">
      <div className="container flex items-center gap-5 px-4 md:px-6">
        <DesktopNav />
        <Sheet>
          <SheetTrigger asChild>
            <Button
              variant="outline"
              size="icon"
              className="shrink-0 md:hidden"
            >
              <Menu className="h-5 w-5" />
              <span className="sr-only">{t("Header.menu_open")}</span>
            </Button>
          </SheetTrigger>
          <SheetContent side="left">
            <MobileNav />
          </SheetContent>
        </Sheet>
        <div className="flex w-full items-center gap-4 md:ml-auto md:gap-2 lg:gap-4">
          <div className="ml-auto flex flex-1 justify-end gap-2">
            <NewProcedureButton />
          </div>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                variant="secondary"
                size="icon"
                className="h-8 w-8 rounded-full"
              >
                <UserAvatar user={user} size="md" />
                <span className="sr-only">{t("Header.user_menu")}</span>
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-[200px]">
              <Can I="manage" a="Procedure">
                <DropdownMenuItem asChild>
                  <Link to={routePaths.settings}>{t("Header.settings")}</Link>
                </DropdownMenuItem>
                <DropdownMenuItem asChild>
                  <a
                    href={EXTENSION_STORE_URL}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    {t("Header.chrome_extension")}
                  </a>
                </DropdownMenuItem>
                {agencyExportEnabled && (
                  <DropdownMenuItem asChild>
                    <Link to={routePaths.export}>
                      {t("Header.export_procedures")}
                    </Link>
                  </DropdownMenuItem>
                )}
              </Can>
              <OrganizationSwitcher />
              <DropdownMenuSeparator />
              <DropdownMenuItem asChild>
                <Link to={routePaths.logout}>{t("Header.logout")}</Link>
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>
    </header>
  );
};

export { Header };
